import { getApiUrl } from './api-url.js';
import type {
    SendMessageRequest,
    SendMessageResponse,
    SessionHistoryResponse,
    SessionsResponse,
    Session,
    Memory,
    MemoryResponse,
    MemoriesResponse,
    MemoryCountResponse,
    McpServer,
    McpServersResponse,
    ApiErrorResponse,
    AsyncTask,
    TasksResponse,
    TaskResponse,
    TaskStatsResponse,
} from '@/types/api.js';
import type { Schedule, CreateScheduleInput, UpdateScheduleInput } from '@/stores/schedulerStore.js';

/**
 * Extract the agentDbId and base API URL from the full API URL.
 * URL format: http://host:port/api/{username}/agents/{agentDbId}
 * Returns: { apiBase: 'http://host:port/api', agentDbId: '...' }
 */
function parseAgentUrl(url: string): { apiBase: string; agentDbId: string } {
    // Match pattern: /api/{username}/agents/{agentDbId}
    const match = url.match(/^(https?:\/\/[^/]+\/api)\/[^/]+\/agents\/([^/]+)\/?$/);
    if (match) {
        return { apiBase: match[1], agentDbId: match[2] };
    }
    // Fallback: just /api/agents/{agentDbId}
    const simpleMatch = url.match(/^(https?:\/\/[^/]+\/api)\/agents\/([^/]+)\/?$/);
    if (simpleMatch) {
        return { apiBase: simpleMatch[1], agentDbId: simpleMatch[2] };
    }
    // Default fallback - use URL as-is
    return { apiBase: url, agentDbId: '' };
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public code?: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 0 || response.type === 'opaque') {
        throw new ApiError(
            'CORS error: Unable to connect to API server. Please ensure the server is running and CORS is configured correctly.',
            0,
            'CORS_ERROR'
        );
    }

    const text = await response.text();

    if (!response.ok) {
        let errorData: ApiErrorResponse;
        try {
            if (text) {
                errorData = JSON.parse(text);
            } else {
                throw new Error('Empty response');
            }
        } catch {
            errorData = {
                error: response.statusText || 'Unknown error',
                message: `HTTP ${response.status}`,
            };
        }

        if (response.status === 401) {
            throw new ApiError(errorData.error || 'Unauthorized', 401, errorData.code);
        }

        if (response.status === 403) {
            throw new ApiError(errorData.error || 'Forbidden', 403, errorData.code);
        }

        throw new ApiError(
            errorData.error || errorData.message || 'API request failed',
            response.status,
            errorData.code
        );
    }

    if (!text) {
        return {} as T;
    }

    try {
        return JSON.parse(text);
    } catch {
        throw new ApiError('Invalid JSON response from server', response.status, 'INVALID_JSON');
    }
}

export function createApiClient(
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
) {
    const baseUrl = getApiUrl();

    return {
        // Message endpoints
        async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
            const response = await authenticatedFetch(`${baseUrl}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(request),
            });
            return handleResponse<SendMessageResponse>(response);
        },

        // Session endpoints
        async listSessions(): Promise<Session[]> {
            const response = await authenticatedFetch(`${baseUrl}/sessions`);
            const data = await handleResponse<SessionsResponse>(response);
            return data.sessions;
        },

        async createSession(
            sessionId?: string
        ): Promise<{
            session: { id: string; createdAt: number; lastActivity: number; messageCount: number };
        }> {
            const response = await authenticatedFetch(`${baseUrl}/sessions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionId ? { sessionId } : {}),
            });
            return handleResponse<{
                session: {
                    id: string;
                    createdAt: number;
                    lastActivity: number;
                    messageCount: number;
                };
            }>(response);
        },

        async getSession(sessionId: string): Promise<{ sessionId: string; exists: boolean }> {
            const response = await authenticatedFetch(`${baseUrl}/sessions/${sessionId}`);
            return handleResponse<{ sessionId: string; exists: boolean }>(response);
        },

        async getSessionHistory(sessionId: string): Promise<SessionHistoryResponse> {
            const response = await authenticatedFetch(
                `${baseUrl}/sessions/${sessionId}/history`
            );
            return handleResponse<SessionHistoryResponse>(response);
        },

        async deleteSession(sessionId: string): Promise<void> {
            const response = await authenticatedFetch(`${baseUrl}/sessions/${sessionId}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        async getCurrentSession(): Promise<{
            session: {
                id: string;
                createdAt: number;
                lastActivity: number;
                messageCount: number;
            } | null;
        }> {
            const response = await authenticatedFetch(`${baseUrl}/sessions/current`);
            return handleResponse<{
                session: {
                    id: string;
                    createdAt: number;
                    lastActivity: number;
                    messageCount: number;
                } | null;
            }>(response);
        },

        async resetSession(sessionId?: string): Promise<void> {
            const response = await authenticatedFetch(`${baseUrl}/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionId ? { sessionId } : {}),
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        async generateSessionTitle(sessionId: string): Promise<{ title: string | null; sessionId: string }> {
            const response = await authenticatedFetch(`${baseUrl}/sessions/${sessionId}/generate-title`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            return handleResponse<{ title: string | null; sessionId: string }>(response);
        },

        // Memory endpoints
        async getMemories(options?: {
            tags?: string[];
            source?: 'user' | 'system';
            pinned?: boolean;
            limit?: number;
            offset?: number;
        }): Promise<Memory[]> {
            const params = new URLSearchParams();
            if (options?.tags) {
                params.append('tags', options.tags.join(','));
            }
            if (options?.source) {
                params.append('source', options.source);
            }
            if (options?.pinned !== undefined) {
                params.append('pinned', options.pinned.toString());
            }
            if (options?.limit) {
                params.append('limit', options.limit.toString());
            }
            if (options?.offset) {
                params.append('offset', options.offset.toString());
            }

            const url = `${baseUrl}/memory${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await authenticatedFetch(url);
            const data = await handleResponse<MemoriesResponse>(response);
            return data.memories;
        },

        async getMemory(id: string): Promise<Memory> {
            const response = await authenticatedFetch(`${baseUrl}/memory/${id}`);
            const data = await handleResponse<MemoryResponse>(response);
            return data.memory;
        },

        async createMemory(input: {
            content: string;
            tags?: string[];
            source?: 'user' | 'system';
            pinned?: boolean;
        }): Promise<Memory> {
            const response = await authenticatedFetch(`${baseUrl}/memory`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input),
            });
            const data = await handleResponse<MemoryResponse>(response);
            return data.memory;
        },

        async updateMemory(
            id: string,
            updates: {
                content?: string;
                tags?: string[];
                source?: 'user' | 'system';
                pinned?: boolean;
            }
        ): Promise<Memory> {
            const response = await authenticatedFetch(`${baseUrl}/memory/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            const data = await handleResponse<MemoryResponse>(response);
            return data.memory;
        },

        async deleteMemory(id: string): Promise<void> {
            const response = await authenticatedFetch(`${baseUrl}/memory/${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        async getMemoryCount(options?: {
            tags?: string[];
            source?: 'user' | 'system';
            pinned?: boolean;
        }): Promise<number> {
            const params = new URLSearchParams();
            if (options?.tags) {
                params.append('tags', options.tags.join(','));
            }
            if (options?.source) {
                params.append('source', options.source);
            }
            if (options?.pinned !== undefined) {
                params.append('pinned', options.pinned.toString());
            }

            const url = `${baseUrl}/memory/count${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await authenticatedFetch(url);
            const data = await handleResponse<MemoryCountResponse>(response);
            return data.count;
        },

        // MCP Server endpoints
        async getMcpServers(): Promise<McpServer[]> {
            const response = await authenticatedFetch(`${baseUrl}/mcp/servers`);
            const data = await handleResponse<McpServersResponse>(response);
            return data.servers;
        },

        async addMcpServer(
            name: string,
            config: unknown
        ): Promise<{ status: string; name: string }> {
            const response = await authenticatedFetch(`${baseUrl}/mcp/servers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, config }),
            });
            return handleResponse<{ status: string; name: string }>(response);
        },

        async deleteMcpServer(serverId: string): Promise<void> {
            const response = await authenticatedFetch(`${baseUrl}/mcp/servers/${serverId}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        async restartMcpServer(serverId: string): Promise<{ status: string; name: string }> {
            const response = await authenticatedFetch(
                `${baseUrl}/mcp/servers/${serverId}/restart`,
                {
                    method: 'POST',
                }
            );
            return handleResponse<{ status: string; name: string }>(response);
        },

        // Tool execution endpoints
        async executeTool(
            toolName: string,
            args: Record<string, unknown>
        ): Promise<{ success: boolean; data?: unknown; error?: string }> {
            const response = await authenticatedFetch(`${baseUrl}/tools/${toolName}/execute`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(args),
            });
            return handleResponse<{ success: boolean; data?: unknown; error?: string }>(response);
        },

        // Async A2A Task endpoints
        async getTasks(options?: {
            status?: string;
            sessionId?: string;
            active?: boolean;
        }): Promise<AsyncTask[]> {
            const params = new URLSearchParams();
            if (options?.status) {
                params.append('status', options.status);
            }
            if (options?.sessionId) {
                params.append('sessionId', options.sessionId);
            }
            if (options?.active !== undefined) {
                params.append('active', options.active.toString());
            }

            const url = `${baseUrl}/tasks${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await authenticatedFetch(url);
            const data = await handleResponse<TasksResponse>(response);
            return data.tasks;
        },

        async getTask(taskId: string): Promise<AsyncTask> {
            const response = await authenticatedFetch(`${baseUrl}/tasks/${taskId}`);
            const data = await handleResponse<TaskResponse>(response);
            return data.task;
        },

        async deleteTask(taskId: string): Promise<void> {
            const response = await authenticatedFetch(`${baseUrl}/tasks/${taskId}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        async getTaskStats(): Promise<TaskStatsResponse> {
            const response = await authenticatedFetch(`${baseUrl}/tasks/stats`);
            return handleResponse<TaskStatsResponse>(response);
        },

        // Schedule endpoints
        // Note: Schedules API uses /api/agents/:agentDbId/schedules path structure
        async listSchedules(): Promise<Schedule[]> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            console.log('[API] listSchedules - parsed URL:', { apiBase, agentDbId, baseUrl });
            if (!agentDbId) {
                console.error('[API] listSchedules - Cannot determine agent ID from API URL:', baseUrl);
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const schedulesUrl = `${apiBase}/agents/${agentDbId}/schedules`;
            console.log('[API] listSchedules - fetching from:', schedulesUrl);
            const response = await authenticatedFetch(schedulesUrl);
            const rawData = await response.clone().json();
            console.log('[API] listSchedules - raw response:', rawData);
            const data = await handleResponse<{ success: boolean; data: Schedule[] }>(response);
            console.log('[API] listSchedules - parsed data:', data, 'returning:', data.data || []);
            return data.data || [];
        },

        async createSchedule(input: CreateScheduleInput): Promise<Schedule> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            if (!agentDbId) {
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const schedulesUrl = `${apiBase}/agents/${agentDbId}/schedules`;
            const response = await authenticatedFetch(schedulesUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input),
            });
            const data = await handleResponse<{ success: boolean; data: Schedule }>(response);
            return data.data;
        },

        async getSchedule(scheduleId: string): Promise<Schedule> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            if (!agentDbId) {
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const schedulesUrl = `${apiBase}/agents/${agentDbId}/schedules/${scheduleId}`;
            const response = await authenticatedFetch(schedulesUrl);
            const data = await handleResponse<{ success: boolean; data: Schedule }>(response);
            return data.data;
        },

        async updateSchedule(scheduleId: string, updates: Partial<UpdateScheduleInput>): Promise<Schedule> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            if (!agentDbId) {
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const schedulesUrl = `${apiBase}/agents/${agentDbId}/schedules/${scheduleId}`;
            const response = await authenticatedFetch(schedulesUrl, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            const data = await handleResponse<{ success: boolean; data: Schedule }>(response);
            return data.data;
        },

        async deleteSchedule(scheduleId: string): Promise<void> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            if (!agentDbId) {
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const schedulesUrl = `${apiBase}/agents/${agentDbId}/schedules/${scheduleId}`;
            const response = await authenticatedFetch(schedulesUrl, {
                method: 'DELETE',
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        async triggerSchedule(scheduleId: string): Promise<any> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            if (!agentDbId) {
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const triggerUrl = `${apiBase}/agents/${agentDbId}/schedules/${scheduleId}/trigger`;
            const response = await authenticatedFetch(triggerUrl, {
                method: 'POST',
            });
            const data = await handleResponse<{ success: boolean; data: any }>(response);
            return data.data;
        },

        async getScheduleHistory(scheduleId: string, limit?: number): Promise<any[]> {
            const { apiBase, agentDbId } = parseAgentUrl(baseUrl);
            if (!agentDbId) {
                throw new ApiError('Cannot determine agent ID from API URL', 400, 'INVALID_URL');
            }
            const params = limit ? `?limit=${limit}` : '';
            const historyUrl = `${apiBase}/agents/${agentDbId}/schedules/${scheduleId}/history${params}`;
            const response = await authenticatedFetch(historyUrl);
            const data = await handleResponse<{ success: boolean; data: any[] }>(response);
            return data.data || [];
        },

        // Prompts endpoints
        async listPrompts(): Promise<Record<string, any>> {
            const response = await authenticatedFetch(`${baseUrl}/prompts`);
            const data = await handleResponse<{ prompts: Record<string, any> }>(response);
            return data.prompts;
        },

        async getPrompt(name: string): Promise<any> {
            const response = await authenticatedFetch(`${baseUrl}/prompts/${encodeURIComponent(name)}`);
            const data = await handleResponse<{ prompt: any }>(response);
            return data.prompt;
        },

        async resolvePrompt(name: string, args?: Record<string, unknown>, context?: string): Promise<{ text: string; resources: string[] }> {
            const params = new URLSearchParams();
            if (args) {
                params.append('args', JSON.stringify(args));
            }
            if (context) {
                params.append('context', context);
            }
            const url = `${baseUrl}/prompts/${encodeURIComponent(name)}/resolve${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await authenticatedFetch(url);
            return handleResponse<{ text: string; resources: string[] }>(response);
        },

        async createCustomPrompt(input: {
            name: string;
            title?: string;
            description?: string;
            content: string;
            arguments?: Array<{ name: string; description?: string; required?: boolean }>;
            resource?: {
                data: string;
                mimeType: string;
                filename?: string;
            };
        }): Promise<any> {
            const response = await authenticatedFetch(`${baseUrl}/prompts/custom`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input),
            });
            const data = await handleResponse<{ prompt: any }>(response);
            return data.prompt;
        },

        async deleteCustomPrompt(name: string): Promise<void> {
            const response = await authenticatedFetch(`${baseUrl}/prompts/custom/${encodeURIComponent(name)}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                await handleResponse(response);
            }
        },

        // LLM endpoints
        async getLLMCatalog(mode: 'grouped' | 'flat' = 'grouped'): Promise<any> {
            const params = new URLSearchParams({ mode });
            const response = await authenticatedFetch(`${baseUrl}/llm/catalog?${params.toString()}`);
            return handleResponse<any>(response);
        },

        async getCurrentLLM(sessionId?: string): Promise<any> {
            const params = sessionId ? new URLSearchParams({ sessionId }) : '';
            const url = `${baseUrl}/llm/current${params ? `?${params.toString()}` : ''}`;
            const response = await authenticatedFetch(url);
            return handleResponse<any>(response);
        },

        async switchLLM(payload: {
            provider?: string;
            model?: string;
            router?: string;
            baseURL?: string;
            sessionId?: string;
        }): Promise<any> {
            const response = await authenticatedFetch(`${baseUrl}/llm/switch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            return handleResponse<any>(response);
        },
    };
}

export type ApiClient = ReturnType<typeof createApiClient>;
